import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Edit, Trash2, Search, MoreVertical, Package, Weight, DollarSign, TrendingUp, ChevronLeft, ChevronRight, FileDown, Upload, X, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import EmptyState from '../components/EmptyState';

const SEGMENTOS = ['CAIXA', 'CHAPA', 'CORTE VINCO', 'SIMPLEX'];

const fmt = (v, c = 2) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c }).format(v || 0);

const Materiais = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [materiais, setMateriais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSegmento, setFilterSegmento] = useState('TODOS');
  const [filterRepresentada, setFilterRepresentada] = useState('');
  const [filterCliente, setFilterCliente] = useState('');
  const [clientes, setClientes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const importInputRef = useRef(null);

  useEffect(() => { fetchClientes(); }, []);
  useEffect(() => { fetchMateriais(filterCliente); }, [filterCliente]);
  useEffect(() => { setCurrentPage(1); }, [searchTerm, filterSegmento, filterRepresentada, filterCliente]);

  const representadasUnicas = useMemo(() => [
    ...new Set(materiais.map(m => m.representada_nome).filter(Boolean))
  ].sort(), [materiais]);

  const fetchClientes = async () => {
    try {
      const r = await api.get('/clientes');
      const lista = Array.isArray(r.data) ? r.data : [];
      setClientes(lista.sort((a, b) => (a.nome_fantasia || a.nome || '').localeCompare(b.nome_fantasia || b.nome || '')));
    } catch {}
  };

  const fetchMateriais = async (clienteId = '') => {
    try {
      const params = clienteId ? { cliente_id: clienteId } : {};
      const r = await api.get('/materiais', { params });
      setMateriais(Array.isArray(r.data) ? r.data : []);
    } catch { toast.error('Erro ao carregar catálogo'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!isAdmin) return;
    if (window.confirm('Excluir este material permanentemente?')) {
      try { await api.delete(`/materiais/${id}`); toast.success('Material removido!'); fetchMateriais(); }
      catch { toast.error('Erro ao deletar'); }
    }
  };

  const handleExportPDF = async () => {
    setPdfLoading(true);
    try {
      const clienteSelecionado = filterCliente ? clientes.find(c => c.id === filterCliente) : null;
      const clienteNome = clienteSelecionado ? (clienteSelecionado.nome_fantasia || clienteSelecionado.nome || '') : '';
      const r = await api.post('/export/materiais-pdf', { ids: filtradosPorBusca.map(m => m.id), cliente_nome: clienteNome }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `materiais_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao gerar PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleImportarPlanilha = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    let importados = 0, duplicados = 0, erros = 0;
    const dupsDetail = [], errosDetail = [];
    let totalRows = 0;

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const sheetName = wb.SheetNames.includes('Materiais') ? 'Materiais' : wb.SheetNames[0];
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { defval: '' });

      if (!rows.length) { toast.error('Planilha vazia ou sem dados.'); return; }

      totalRows = rows.length;
      setImportLoading(true);
      setImportProgress({ current: 0, total: totalRows });

      const existingFEs = new Set(materiais.map(m => (m.numero_fe || '').trim().toUpperCase()));

      const get = (row, key) => {
        const k = Object.keys(row).find(k => k.trim().toUpperCase() === key);
        return k ? String(row[k] || '').trim() : '';
      };
      const getNum = (row, key) => {
        const v = get(row, key);
        return v !== '' ? (parseFloat(v.replace(',', '.')) || 0) : 0;
      };

      for (let i = 0; i < rows.length; i++) {
        setImportProgress({ current: i + 1, total: totalRows });
        const row = rows[i];
        const fe = get(row, 'FE');
        const nome = get(row, 'NOME');

        if (!fe || !nome) {
          erros++;
          errosDetail.push(`Linha ${i + 2}: FE ou NOME ausente`);
          continue;
        }

        const feNorm = fe.trim().toUpperCase();
        if (existingFEs.has(feNorm)) {
          duplicados++;
          dupsDetail.push(`FE ${fe} — ${nome}`);
          continue;
        }

        const segRaw = get(row, 'SEGMENTO').toUpperCase();
        const segmento = ['CAIXA', 'CHAPA', 'CORTE VINCO', 'SIMPLEX'].includes(segRaw) ? segRaw : '';
        const ipiRaw = get(row, 'IPI_PCT');
        const ipi = ipiRaw !== '' ? (parseFloat(ipiRaw.replace(',', '.')) || 0) : null;

        try {
          await api.post('/materiais', {
            nome,
            numero_fe: fe,
            peso_unit: getNum(row, 'PESO_KG'),
            preco_unit: getNum(row, 'MILHEIRO'),
            comissao: getNum(row, 'COMISSAO_PCT'),
            segmento,
            ipi,
            representada_nome: get(row, 'REPRESENTADA'),
            comprimento: getNum(row, 'COMPRIMENTO'),
            largura: getNum(row, 'LARGURA'),
            altura: getNum(row, 'ALTURA'),
            gramatura: getNum(row, 'GRAMATURA'),
          });
          importados++;
          existingFEs.add(feNorm);
        } catch (err) {
          if (err.response?.status === 409) {
            duplicados++;
            dupsDetail.push(`FE ${fe} — ${nome}`);
          } else {
            erros++;
            errosDetail.push(`FE ${fe}: ${err.response?.data?.detail || 'Erro desconhecido'}`);
          }
        }
      }

      if (importados > 0) fetchMateriais();
      setImportResult({ total: totalRows, importados, duplicados, erros, dupsDetail, errosDetail });
    } catch {
      toast.error('Erro ao processar planilha. Verifique se é um .xlsx válido.');
    } finally {
      setImportLoading(false);
    }
  };

  const filtradosPorBusca = materiais.filter(m =>
    !searchTerm ||
    (m.nome||m.descricao||'').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (m.numero_fe||m.codigo||'').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filtered = filtradosPorBusca.filter(m => {
    const matchSegmento = filterSegmento === 'TODOS' || m.segmento === filterSegmento;
    const matchRepresentada = !filterRepresentada || m.representada_nome === filterRepresentada;
    return matchSegmento && matchRepresentada;
  });

  const contadores = filtradosPorBusca.reduce((acc, m) => { acc[m.segmento] = (acc[m.segmento]||0)+1; return acc; }, {});

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const filteredPagina = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin"/>
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando Catálogo...</p>
    </div>
  );

  return (
    <div className="p-4 md:p-6 bg-[#E9EEF2] min-h-screen">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Catálogo Base (FE)</h1>
          <p className="text-xs text-slate-500 font-semibold mt-0.5">Produtos & Precificação · {filtered.length} registros</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center bg-white border border-slate-200 rounded-lg h-9 px-3 gap-2 shadow-sm">
            <Search size={13} className="text-slate-400"/>
            <input placeholder="Buscar por FE ou descrição..." className="text-xs font-semibold bg-transparent outline-none w-44 text-slate-700"
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}/>
          </div>
          <select value={filterRepresentada}
            onChange={e => setFilterRepresentada(e.target.value)}
            className="border border-slate-200 rounded-lg bg-white px-3 h-9 text-xs font-semibold text-slate-700 outline-none focus:border-[#0A3D73] transition-all">
            <option value="">Todas representadas</option>
            {representadasUnicas.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <select value={filterCliente}
            onChange={e => setFilterCliente(e.target.value)}
            className="border border-slate-200 rounded-lg bg-white px-3 h-9 text-xs font-semibold text-slate-700 outline-none focus:border-[#0A3D73] transition-all">
            <option value="">Todos os clientes</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.nome}</option>)}
          </select>
          <button
            onClick={handleExportPDF}
            disabled={pdfLoading}
            className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <FileDown size={14}/> {pdfLoading ? 'Gerando...' : 'PDF'}
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => importInputRef.current?.click()}
                disabled={importLoading}
                className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload size={14}/> Importar Planilha
              </button>
              <button onClick={() => navigate('/materiais/novo')}
                className="flex items-center gap-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors">
                <Plus size={14}/> Novo FE
              </button>
            </>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        {[
          {label:'Total de FEs', value:filtered.length, icon:Package, cor:'#1E40AF', fundo:'#EFF6FF'},
          {label:'Preço Médio Milheiro', value:`R$ ${fmt(filtered.reduce((a,m)=>a+(m.preco_unit||0),0)/(filtered.length||1))}`, icon:DollarSign, cor:'#166534', fundo:'#DCFCE7'},
          {label:'Comissão Média', value:`${fmt(filtered.reduce((a,m)=>a+(m.comissao||0),0)/(filtered.length||1))}%`, icon:TrendingUp, cor:'#0891B2', fundo:'#ECFEFF'},
          {label:'Peso Médio Unit.', value:`${fmt(filtered.reduce((a,m)=>a+(m.peso_unit||0),0)/(filtered.length||1),4)} kg`, icon:Weight, cor:'#374151', fundo:'#F9FAFB'},
        ].map((k,i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{k.label}</p>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{background:k.fundo}}>
                <k.icon size={13} style={{color:k.cor}}/>
              </div>
            </div>
            <p className="text-lg font-black" style={{color:k.cor}}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* FILTROS SEGMENTO */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['TODOS',...SEGMENTOS].map(s => (
          <button key={s} onClick={() => setFilterSegmento(s)}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${filterSegmento===s?'bg-[#0A3D73] text-white border-[#0A3D73] shadow-sm':'bg-white text-slate-600 border-slate-200 hover:border-[#0A3D73]'}`}>
            {s} <span className="opacity-60">({s==='TODOS'?filtradosPorBusca.length:contadores[s]||0})</span>
          </button>
        ))}
      </div>

      {/* TABELA */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-[#0A3D73] text-white text-xs font-black uppercase tracking-wide">
                <th className="px-4 py-3">FE</th>
                <th className="px-4 py-3">Descrição Técnica</th>
                <th className="px-4 py-3 text-center">Segmento</th>
                <th className="px-4 py-3">Dimensões</th>
                <th className="px-4 py-3">Representada</th>
                <th className="px-4 py-3 text-right">Peso Unit.</th>
                <th className="px-4 py-3 text-right">R$ Milheiro</th>
                <th className="px-4 py-3 text-right">Fator R$/KG</th>
                <th className="px-4 py-3 text-right">Comissão %</th>
                {isAdmin && <th className="px-4 py-3 text-center">Ações</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={isAdmin?10:9}>
                  <EmptyState
                    icon={
                      <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
                        <path d="M10 20h52a2 2 0 012 2v6H8v-6a2 2 0 012-2z" stroke="#4A90D9" strokeWidth="2" strokeLinejoin="round"/>
                        <path d="M14 28h44v30a2 2 0 01-2 2H16a2 2 0 01-2-2V28z" stroke="#4A90D9" strokeWidth="2" strokeLinejoin="round"/>
                        <path d="M29 28v-8h14v8" stroke="#4A90D9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <line x1="36" y1="38" x2="36" y2="50" stroke="#7BB8F0" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="3 2"/>
                      </svg>
                    }
                    title="Nenhum material cadastrado"
                    subtitle="Cadastre os produtos da sua carteira"
                    actionLabel={isAdmin ? '+ Novo Material' : undefined}
                    onAction={isAdmin ? () => navigate('/materiais/novo') : undefined}
                  />
                </td></tr>
              ) : filteredPagina.map((m, idx) => {
                const fator = m.peso_unit > 0 ? m.preco_unit / (m.peso_unit * 1000) : 0;
                const dimDisplay = (() => {
                  if (!m.comprimento) return '—';
                  if (m.segmento === 'CAIXA' || m.segmento === 'CORTE VINCO')
                    return `${m.comprimento}×${m.largura}×${m.altura}mm`;
                  if (m.segmento === 'CHAPA')
                    return `${m.comprimento}×${m.largura}mm`;
                  return '—';
                })();
                return (
                  <tr key={m.id} onClick={() => isAdmin && navigate(`/materiais/editar/${m.id}`)}
                    className={`text-xs border-b border-slate-100 transition-colors
                      ${idx%2===0?'bg-white':'bg-slate-50/60'}
                      ${isAdmin?'hover:bg-blue-50 cursor-pointer':''}`}>
                    <td className="px-4 py-2.5 font-mono font-black text-[#0A3D73] whitespace-nowrap">{m.numero_fe||m.codigo}</td>
                    <td className="px-4 py-2.5 font-bold uppercase max-w-[200px] truncate">{m.nome||m.descricao}</td>
                    <td className="px-4 py-2.5 text-center">
                      {m.segmento ? (
                        <span className="px-2 py-0.5 text-xs font-black border bg-slate-100 text-slate-600 border-slate-200 rounded-full whitespace-nowrap">
                          {m.segmento}{m.ipi != null ? ` · ${m.ipi % 1 === 0 ? m.ipi : fmt(m.ipi, 2)}%` : ''}
                        </span>
                      ) : '---'}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-slate-500 whitespace-nowrap">{dimDisplay}</td>
                    <td className="px-4 py-2.5 font-semibold text-slate-600 whitespace-nowrap max-w-[120px] truncate">{m.representada_nome || '—'}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-slate-600 whitespace-nowrap">{fmt(m.peso_unit,4)} kg</td>
                    <td className="px-4 py-2.5 text-right font-bold whitespace-nowrap">R$ {fmt(m.preco_unit)}</td>
                    <td className="px-4 py-2.5 text-right font-black text-[#0A3D73] whitespace-nowrap">R$ {fmt(fator)}</td>
                    <td className="px-4 py-2.5 text-right font-black text-emerald-700 whitespace-nowrap">{fmt(m.comissao)}%</td>
                    {isAdmin && (
                      <td className="px-4 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="h-7 w-7 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors">
                              <MoreVertical size={14} className="text-slate-400"/>
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="rounded-xl border-slate-200 shadow-xl bg-white">
                            <DropdownMenuItem onClick={() => navigate(`/materiais/editar/${m.id}`)} className="text-xs font-bold gap-2 cursor-pointer p-3 rounded-lg">
                              <Edit size={13} className="text-[#0A3D73]"/> Alterar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(m.id)} className="text-xs font-bold gap-2 cursor-pointer text-red-600 p-3 rounded-lg">
                              <Trash2 size={13}/> Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* PAGINAÇÃO */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-white">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-500">Exibir:</span>
              <select
                value={itemsPerPage}
                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="border border-slate-200 rounded-lg text-xs font-bold bg-white px-2 h-8 outline-none focus:border-[#0A3D73] cursor-pointer">
                {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A3D73] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#082D54] transition-colors">
                <ChevronLeft size={14}/>
              </button>
              <span className="text-xs font-black text-slate-600">
                Página {currentPage} de {totalPages} · {filtered.length} registros
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A3D73] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#082D54] transition-colors">
                <ChevronRight size={14}/>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── IMPORTAÇÃO — input oculto ── */}
      <input
        ref={importInputRef}
        type="file"
        accept=".xlsx"
        className="hidden"
        onChange={handleImportarPlanilha}
      />

      {/* ── IMPORTAÇÃO — overlay de progresso ── */}
      {importLoading && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="text-center mb-4">
              <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin mx-auto mb-3"/>
              <p className="text-sm font-black text-slate-700">Importando planilha...</p>
              <p className="text-xs text-slate-400 mt-1 font-semibold">{importProgress.current} de {importProgress.total} linhas</p>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2.5">
              <div
                className="bg-[#0A3D73] h-2.5 rounded-full transition-all duration-200"
                style={{ width: importProgress.total ? `${(importProgress.current / importProgress.total) * 100}%` : '0%' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── IMPORTAÇÃO — modal de resultado ── */}
      {importResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-sm font-black uppercase tracking-wide text-slate-800">Resultado da Importação</h2>
              <button onClick={() => setImportResult(null)} className="p-1 rounded-lg hover:bg-slate-100 transition-colors">
                <X size={16} className="text-slate-400"/>
              </button>
            </div>
            <div className="p-6 space-y-2.5">
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0"/>
                <p className="text-sm font-black text-emerald-700">✅ {importResult.importados} importado(s) com sucesso</p>
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                <AlertTriangle size={18} className="text-amber-600 flex-shrink-0"/>
                <p className="text-sm font-black text-amber-700">⚠️ {importResult.duplicados} ignorado(s) — já existia(m)</p>
              </div>
              <div className="flex items-center gap-3 p-3 bg-red-50 rounded-xl border border-red-100">
                <XCircle size={18} className="text-red-600 flex-shrink-0"/>
                <p className="text-sm font-black text-red-700">❌ {importResult.erros} erro(s)</p>
              </div>

              {importResult.dupsDetail.length > 0 && (
                <div className="pt-1">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">Ignorados (já existiam)</p>
                  <div className="max-h-28 overflow-y-auto space-y-1 pr-1">
                    {importResult.dupsDetail.map((d, i) => (
                      <p key={i} className="text-xs text-amber-700 bg-amber-50 border border-amber-100 px-3 py-1.5 rounded-lg">{d}</p>
                    ))}
                  </div>
                </div>
              )}

              {importResult.errosDetail.length > 0 && (
                <div className="pt-1">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">Detalhes dos Erros</p>
                  <div className="max-h-28 overflow-y-auto space-y-1 pr-1">
                    {importResult.errosDetail.map((e, i) => (
                      <p key={i} className="text-xs text-red-700 bg-red-50 border border-red-100 px-3 py-1.5 rounded-lg">{e}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 pb-5">
              <button
                onClick={() => setImportResult(null)}
                className="w-full bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase h-9 rounded-lg transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Materiais;
